import { useCallback, useState } from "react"

import { toast } from "react-toastify"
import { concat, of } from "rxjs"
import { catchError, finalize, map } from "rxjs/operators"

import NotificationContactData from "./NotificationContactData"
import { SaveNotificationContactObservable } from "./NotificationContactsProcess"
import FormatError from "../../../formatters/FormatError"
import handleAPIError from "../../../utils/handleAPIError"
import ApiResult from "../../ApiResult"

/**
 * Hook to handle saving a notification contact.
 *
 * @param {((result?: T) => void) | undefined} callback - A callback to be called when the request is finished.
 *  The callback will receive the request result as an argument if the request was successful.
 * @returns {{ saving: boolean, error: string, result: T | undefined, submitContact: (params: NotificationContactData) => void }}
 *   An object with the following properties:
 *   - saving: A boolean indicating if the request is in progress.
 *   - error: An error message if the request failed.
 *   - result: The result of the request if it was successful.
 *   - submitContact: A function to submit the request.
 */
function useNotificationContactSave<T>(callback?: (result?: T) => void): {
  saving: boolean
  error: string
  result: T | undefined
  submitContact: (params: NotificationContactData) => void
} {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>("")
  const [result, setResult] = useState<T | undefined>(undefined)

  const submitContact = useCallback(
    (params: NotificationContactData) => {
      setSaving(true)
      setResult(undefined)
      setError("")

      const observable = concat(
        of({ loading: true }),
        SaveNotificationContactObservable(params).pipe(
          map((res) => {
            return { loading: false, result: res } as unknown as ApiResult<T>
          }),
          catchError((err) => {
            setError(FormatError(err))
            handleAPIError(err)
            return of({ loading: false, result: undefined, error: err })
          }),
          finalize(() => {
            setSaving(false)
            if (!error) {
              callback?.(result)
            }
          })
        )
      )

      const subscription = observable.subscribe(({ loading, result }: ApiResult<T>) => {
        setSaving(loading)
        if (!loading && result) {
          setResult(result)
          toast.success(result)
        }
      })

      return () => subscription.unsubscribe()
    },
    [callback, error, result]
  )

  return { saving, error, result, submitContact }
}

export default useNotificationContactSave
